import {
  CoreLanguageType,
  LanguageCode,
  LanguageType,
  LocaleDetails,
  proxyLanguageRecord,
} from 'shared/src/types/languages.type';

/**
 * Copy for the animated 2D country map on /region — the surface that opens
 * after the globe flies to a country and asks the visitor to pick a province,
 * state or region (or opt out with "All of {country}").
 *
 * Deliberately NOT `server-only`: unlike region-ui-content.ts (which must be
 * sliced server-side), this dictionary is small enough that a client component
 * can import it directly without shipping a translation payload worth worrying
 * about.
 *
 * Every core language is filled in. It used to carry en/fr/es only and proxy
 * the other 21 to English, which produced sentences half-translated against a
 * localized country name — "Choose your area in Canadá".
 *
 * Placeholders are `{name}` tokens resolved by `fillRegionMapString`. Keep the
 * token spelling identical across languages — a translated string that renames
 * `{country}` silently renders the literal brace text.
 *
 * ⚠ NO EM DASHES in any of these strings. They are UI labels, often truncated
 * mid-line, and the dash reads as a rendering fault at small sizes. Use a
 * comma or a full stop.
 */
export type RegionMapUiContent = {
  /** Panel heading while a country is open. `{country}` */
  chooseAreaTitle: string;
  /** One line under the heading explaining why the choice matters. */
  chooseAreaSubtitle: string;
  /** The one-click escape hatch, shown first in the list. `{country}` */
  allOfCountry: string;
  /** Sub-label under the escape hatch. */
  allOfCountryHint: string;
  /**
   * Divider between the whole-country card and the subregion list. This is the
   * line that makes the choice a visible fork rather than an implied one — the
   * escape hatch used to read as just another list row.
   */
  orChooseArea: string;
  /** Back one step: country map -> globe -> the page the visitor came from. */
  back: string;
  /** Controls hint under the 3D globe. Was hardcoded English for every locale. */
  globeHint: string;
  /** Suspense fallback while the 3D globe chunk loads. */
  globeLoading: string;
  /** While the country geometry is downloading. */
  loading: string;
  /** Geometry failed to load — the list still works, so this is not fatal. */
  loadError: string;
  /** Confirm CTA after a Places / detect resolution. `{city}` `{area}` */
  useCityInArea: string;
  /** Same, for a city we could not attach to a subregion. `{city}` */
  useCity: string;
  /** Eyebrow above the confirm CTA. */
  weFoundYou: string;
  /** Filter box over long subregion lists. */
  filterPlaceholder: string;
  /** Empty state for the filter. */
  noMatches: string;
  /** Accessible name of the map region. `{country}` */
  mapAriaLabel: string;
  /** Accessible name of one subdivision. `{name}` `{country}` */
  subregionAriaLabel: string;
  /** Accessible name of the subdivision list. `{country}` */
  listAriaLabel: string;
  /** Second line of the hover tooltip. */
  selectHint: string;
  /** Accessible label for the resolved-location pin. `{label}` */
  pinAriaLabel: string;

  /* ── The subregion easter egg (Epic 6) ─────────────────────────────────────
   * Shown once, over the map, when someone picks a place that has its own
   * strong local identity. It is a wink, not a funnel: it must read as warm,
   * take one tap to leave, and never argue.
   *
   * ⚠ FRENCH CARRIES A PREPOSITION. `subregionPromptTitle` says "au {name}",
   * which is correct for "au Québec" and for every place the easter egg can
   * currently reach. Enabling a French-speaking subregion whose name takes a
   * different article ("en Île-de-France") means moving the preposition into
   * the data rather than translating around it. Same for `{country}`: "au
   * Canada" and "au Royaume-Uni" are right, "au France" would not be.
   */
  /** Small line above the title — the "this is a treat" signal. */
  subregionPromptEyebrow: string;
  /** Headline. `{name}` */
  subregionPromptTitle: string;
  /** One sentence explaining the choice. `{name}` `{country}` */
  subregionPromptBody: string;
  /** Accept: this place's products first. `{name}` */
  subregionPromptLocalFirst: string;
  /** Decline: nationwide products, local display. `{country}` */
  subregionPromptCountryFirst: string;
  /** Reassurance under the buttons. */
  subregionPromptFootnote: string;
  /** Accessible name of the close (X) control. */
  subregionPromptDismiss: string;
  /** Accessible name of the dialog itself. `{name}` `{country}` */
  subregionPromptAriaLabel: string;

  /* ── The standard scope confirmation ──────────────────────────────────────
   * Every OTHER province and state, and unlike the wink it is not a greeting:
   * narrowing to one region narrows the catalog, so we say so once and offer
   * the nationwide shelf, which has more on it. Neutral, and the nationwide
   * option is the recommended one.
   *
   * ⚠ NO PREPOSITION BEFORE `{name}` IN ANY LANGUAGE. The wink only ever has
   * to name Québec, so "au {name}" is safe there. This one names all ~500
   * admin-1 units in 24 countries, where the article is unknowable from a
   * template: "de l'Ontario" but "du Québec", "della Lombardia" but "di
   * Sicilia", "da Bahia" but "de São Paulo". A template that guesses is a
   * template that is wrong somewhere.
   *
   * So `{name}` appears ONLY where no article can follow it: after a colon in
   * the title, and as a bare label on the button. Every language whose body
   * would need one says "this region" instead, which costs nothing because the
   * title one line above just named the place. `{country}` is under the same
   * rule, which is why only English names the country at all: the rest say
   * "the whole country".
   */
  /** Small line above the title. Neutral, not a treat. */
  subregionScopeEyebrow: string;
  /** Headline, naming what they just picked. `{name}` */
  subregionScopeTitle: string;
  /** One sentence on the trade. `{name}` `{country}` */
  subregionScopeBody: string;
  /** RECOMMENDED: the nationwide shelf, because it has more on it. `{country}` */
  subregionScopeCountryFirst: string;
  /** The alternative: this place first. `{name}` */
  subregionScopeLocalFirst: string;
};

const regionMapUiContentCore: Partial<Record<CoreLanguageType, RegionMapUiContent>> & {
  en: RegionMapUiContent;
} = {
  [LanguageCode.english]: {
    chooseAreaTitle: 'Choose your area in {country}',
    chooseAreaSubtitle: 'Pick a region for local prices, stores and delivery, or take the whole country.',
    allOfCountry: 'All of {country}',
    allOfCountryHint: 'Nationwide results',
    orChooseArea: 'or choose a specific area',
    back: 'Back',
    globeHint: 'Drag to rotate • Scroll to zoom • Click a country to select',
    globeLoading: 'Loading the globe…',
    loading: 'Loading map…',
    loadError: "Couldn't load the map. Pick from the list instead.",
    useCityInArea: 'Use {city}, {area}',
    useCity: 'Use {city}',
    weFoundYou: 'We found you here',
    filterPlaceholder: 'Filter regions…',
    noMatches: 'No matching region',
    mapAriaLabel: 'Map of {country}. Choose an area',
    subregionAriaLabel: '{name}, {country}',
    listAriaLabel: 'Areas in {country}',
    selectHint: 'Click to select',
    pinAriaLabel: 'Your detected location: {label}',
    subregionPromptEyebrow: 'A wink from us',
    subregionPromptTitle: "Looks like you're in {name} 👋",
    subregionPromptBody:
      'We can put {name} products, prices and stores first, or keep showing you everything from across {country}.',
    subregionPromptLocalFirst: 'Put {name} first',
    subregionPromptCountryFirst: 'Show all of {country}',
    subregionPromptFootnote: 'You can change your mind any time.',
    subregionPromptDismiss: 'Close',
    subregionPromptAriaLabel: 'Regional priority: {name} or {country}?',
    subregionScopeEyebrow: 'More to choose from',
    subregionScopeTitle: 'You picked {name}',
    subregionScopeBody:
      'We can lead with the best products from all of {country}, which gives you more to choose from, or put {name} products, prices and stores first.',
    subregionScopeCountryFirst: 'Show the best of {country}',
    subregionScopeLocalFirst: 'Put {name} first',
  },
  [LanguageCode.french]: {
    chooseAreaTitle: 'Choisissez votre région en {country}',
    chooseAreaSubtitle:
      'Choisissez une région pour des prix, magasins et délais locaux, ou prenez tout le pays.',
    allOfCountry: 'Tout le pays : {country}',
    allOfCountryHint: 'Résultats nationaux',
    orChooseArea: 'ou choisissez une région précise',
    back: 'Retour',
    globeHint: 'Faites glisser pour tourner • Défilez pour zoomer • Cliquez sur un pays',
    globeLoading: 'Chargement du globe…',
    loading: 'Chargement de la carte…',
    loadError: 'Carte indisponible. Choisissez dans la liste.',
    useCityInArea: 'Utiliser {city}, {area}',
    useCity: 'Utiliser {city}',
    weFoundYou: 'Nous vous situons ici',
    filterPlaceholder: 'Filtrer les régions…',
    noMatches: 'Aucune région correspondante',
    mapAriaLabel: 'Carte de {country}. Choisissez une région',
    subregionAriaLabel: '{name}, {country}',
    listAriaLabel: 'Régions de {country}',
    selectHint: 'Cliquez pour sélectionner',
    pinAriaLabel: 'Votre position détectée : {label}',
    subregionPromptEyebrow: "Petit clin d'œil",
    subregionPromptTitle: 'On dirait bien que vous êtes au {name} 👋',
    subregionPromptBody:
      "On peut mettre les produits, les prix et les magasins d'ici en premier, ou continuer à vous montrer tout ce qui se fait partout au {country}.",
    subregionPromptLocalFirst: "Priorité aux produits d'ici",
    subregionPromptCountryFirst: 'Produits de partout au {country}',
    subregionPromptFootnote: "Vous pourrez changer d'idée quand vous voulez.",
    subregionPromptDismiss: 'Fermer',
    subregionPromptAriaLabel: 'Priorité régionale : {name} ou tout le {country} ?',
    subregionScopeEyebrow: 'Plus de choix',
    subregionScopeTitle: 'Votre choix : {name}',
    subregionScopeBody:
      'On peut commencer par les meilleurs produits de tout le pays, ce qui vous donne plus de choix, ou placer les produits, prix et magasins de cette région en premier.',
    subregionScopeCountryFirst: 'Voir le meilleur du pays',
    subregionScopeLocalFirst: 'Priorité : {name}',
  },
  [LanguageCode.spanish]: {
    chooseAreaTitle: 'Elige tu zona en {country}',
    chooseAreaSubtitle:
      'Elige una región para precios, tiendas y entregas locales, o quédate con todo el país.',
    allOfCountry: 'Todo {country}',
    allOfCountryHint: 'Resultados de todo el país',
    orChooseArea: 'o elige una zona concreta',
    back: 'Volver',
    globeHint: 'Arrastra para girar • Desplázate para acercar • Haz clic en un país',
    globeLoading: 'Cargando el globo…',
    loading: 'Cargando el mapa…',
    loadError: 'No se pudo cargar el mapa. Elige en la lista.',
    useCityInArea: 'Usar {city}, {area}',
    useCity: 'Usar {city}',
    weFoundYou: 'Te ubicamos aquí',
    filterPlaceholder: 'Filtrar regiones…',
    noMatches: 'Ninguna región coincide',
    mapAriaLabel: 'Mapa de {country}. Elige una zona',
    subregionAriaLabel: '{name}, {country}',
    listAriaLabel: 'Zonas de {country}',
    selectHint: 'Haz clic para seleccionar',
    pinAriaLabel: 'Tu ubicación detectada: {label}',
    subregionPromptEyebrow: 'Un guiño',
    subregionPromptTitle: 'Parece que estás en {name} 👋',
    subregionPromptBody:
      'Podemos poner primero los productos, precios y tiendas de {name}, o seguir mostrándote todo lo de {country}.',
    subregionPromptLocalFirst: 'Primero {name}',
    subregionPromptCountryFirst: 'Mostrar todo {country}',
    subregionPromptFootnote: 'Puedes cambiar de opinión cuando quieras.',
    subregionPromptDismiss: 'Cerrar',
    subregionPromptAriaLabel: 'Prioridad regional: ¿{name} o {country}?',
    subregionScopeEyebrow: 'Más para elegir',
    subregionScopeTitle: 'Tu elección: {name}',
    subregionScopeBody:
      'Podemos empezar por los mejores productos de todo el país, lo que te da más opciones, o poner primero los productos, precios y tiendas de {name}.',
    subregionScopeCountryFirst: 'Ver lo mejor del país',
    subregionScopeLocalFirst: 'Primero {name}',
  },
  [LanguageCode.german]: {
    chooseAreaTitle: 'Wählen Sie Ihre Region in {country}',
    chooseAreaSubtitle:
      'Wählen Sie eine Region für lokale Preise, Geschäfte und Lieferung, oder nehmen Sie das ganze Land.',
    allOfCountry: 'Ganz {country}',
    allOfCountryHint: 'Ergebnisse aus dem ganzen Land',
    orChooseArea: 'oder wählen Sie eine bestimmte Region',
    back: 'Zurück',
    globeHint: 'Ziehen zum Drehen • Scrollen zum Zoomen • Land anklicken',
    globeLoading: 'Globus wird geladen…',
    loading: 'Karte wird geladen…',
    loadError: 'Karte konnte nicht geladen werden. Bitte wählen Sie aus der Liste.',
    useCityInArea: '{city}, {area} verwenden',
    useCity: '{city} verwenden',
    weFoundYou: 'Wir haben Sie hier gefunden',
    filterPlaceholder: 'Regionen filtern…',
    noMatches: 'Keine passende Region',
    mapAriaLabel: 'Karte von {country}. Wählen Sie eine Region',
    subregionAriaLabel: '{name}, {country}',
    listAriaLabel: 'Regionen in {country}',
    selectHint: 'Zum Auswählen klicken',
    pinAriaLabel: 'Ihr ermittelter Standort: {label}',
    subregionPromptEyebrow: 'Ein Augenzwinkern',
    subregionPromptTitle: 'Sieht aus, als wären Sie in {name} 👋',
    subregionPromptBody:
      'Wir können Produkte, Preise und Geschäfte aus {name} zuerst zeigen, oder weiterhin alles aus ganz {country}.',
    subregionPromptLocalFirst: '{name} zuerst',
    subregionPromptCountryFirst: 'Ganz {country} anzeigen',
    subregionPromptFootnote: 'Sie können es jederzeit ändern.',
    subregionPromptDismiss: 'Schließen',
    subregionPromptAriaLabel: 'Regionale Priorität: {name} oder {country}?',
    subregionScopeEyebrow: 'Mehr Auswahl',
    subregionScopeTitle: 'Ihre Wahl: {name}',
    subregionScopeBody:
      'Wir können mit den besten Produkten aus dem ganzen Land beginnen, das gibt Ihnen mehr Auswahl, oder Produkte, Preise und Geschäfte aus dieser Region zuerst zeigen.',
    subregionScopeCountryFirst: 'Das Beste aus dem ganzen Land',
    subregionScopeLocalFirst: '{name} zuerst',
  },
  [LanguageCode.italian]: {
    chooseAreaTitle: 'Scegli la tua zona in {country}',
    chooseAreaSubtitle:
      'Scegli una regione per prezzi, negozi e consegne locali, oppure prendi tutto il paese.',
    allOfCountry: 'Tutto il paese: {country}',
    allOfCountryHint: 'Risultati da tutto il paese',
    orChooseArea: 'oppure scegli una zona specifica',
    back: 'Indietro',
    globeHint: 'Trascina per ruotare • Scorri per ingrandire • Clicca su un paese',
    globeLoading: 'Caricamento del globo…',
    loading: 'Caricamento della mappa…',
    loadError: 'Impossibile caricare la mappa. Scegli dalla lista.',
    useCityInArea: 'Usa {city}, {area}',
    useCity: 'Usa {city}',
    weFoundYou: 'Ti abbiamo trovato qui',
    filterPlaceholder: 'Filtra le regioni…',
    noMatches: 'Nessuna regione corrispondente',
    mapAriaLabel: 'Mappa di {country}. Scegli una zona',
    subregionAriaLabel: '{name}, {country}',
    listAriaLabel: 'Zone di {country}',
    selectHint: 'Clicca per selezionare',
    pinAriaLabel: 'La tua posizione rilevata: {label}',
    subregionPromptEyebrow: 'Un occhiolino',
    subregionPromptTitle: 'Sembra che tu sia in {name} 👋',
    subregionPromptBody:
      'Possiamo mostrare prima prodotti, prezzi e negozi di {name}, oppure continuare a mostrarti tutto da {country}.',
    subregionPromptLocalFirst: 'Prima {name}',
    subregionPromptCountryFirst: 'Mostra tutto il paese',
    subregionPromptFootnote: 'Puoi cambiare idea quando vuoi.',
    subregionPromptDismiss: 'Chiudi',
    subregionPromptAriaLabel: 'Priorità regionale: {name} o {country}?',
    subregionScopeEyebrow: 'Più scelta',
    subregionScopeTitle: 'La tua scelta: {name}',
    subregionScopeBody:
      'Possiamo partire dai migliori prodotti di tutto il paese, così hai più scelta, oppure mettere prima prodotti, prezzi e negozi di questa regione.',
    subregionScopeCountryFirst: 'Il meglio di tutto il paese',
    subregionScopeLocalFirst: 'Priorità: {name}',
  },
  [LanguageCode.portugueseBR]: {
    chooseAreaTitle: 'Escolha sua área em {country}',
    chooseAreaSubtitle:
      'Escolha uma região para preços, lojas e entregas locais, ou fique com o país inteiro.',
    allOfCountry: 'Todo o país: {country}',
    allOfCountryHint: 'Resultados de todo o país',
    orChooseArea: 'ou escolha uma área específica',
    back: 'Voltar',
    globeHint: 'Arraste para girar • Role para ampliar • Clique em um país',
    globeLoading: 'Carregando o globo…',
    loading: 'Carregando o mapa…',
    loadError: 'Não foi possível carregar o mapa. Escolha na lista.',
    useCityInArea: 'Usar {city}, {area}',
    useCity: 'Usar {city}',
    weFoundYou: 'Encontramos você aqui',
    filterPlaceholder: 'Filtrar regiões…',
    noMatches: 'Nenhuma região correspondente',
    mapAriaLabel: 'Mapa de {country}. Escolha uma área',
    subregionAriaLabel: '{name}, {country}',
    listAriaLabel: 'Áreas de {country}',
    selectHint: 'Clique para selecionar',
    pinAriaLabel: 'Sua localização detectada: {label}',
    subregionPromptEyebrow: 'Uma piscadela',
    subregionPromptTitle: 'Parece que você está em {name} 👋',
    subregionPromptBody:
      'Podemos mostrar primeiro produtos, preços e lojas de {name}, ou continuar mostrando tudo de {country}.',
    subregionPromptLocalFirst: '{name} primeiro',
    subregionPromptCountryFirst: 'Mostrar todo o país',
    subregionPromptFootnote: 'Você pode mudar de ideia quando quiser.',
    subregionPromptDismiss: 'Fechar',
    subregionPromptAriaLabel: 'Prioridade regional: {name} ou {country}?',
    subregionScopeEyebrow: 'Mais opções',
    subregionScopeTitle: 'Sua escolha: {name}',
    subregionScopeBody:
      'Podemos começar pelos melhores produtos de todo o país, o que dá mais opções, ou colocar os produtos, preços e lojas desta região em primeiro lugar.',
    subregionScopeCountryFirst: 'Ver o melhor do país',
    subregionScopeLocalFirst: '{name} primeiro',
  },
  [LanguageCode.portuguesePT]: {
    chooseAreaTitle: 'Escolha a sua área em {country}',
    chooseAreaSubtitle:
      'Escolha uma região para preços, lojas e entregas locais, ou fique com o país inteiro.',
    allOfCountry: 'Todo o país: {country}',
    allOfCountryHint: 'Resultados de todo o país',
    orChooseArea: 'ou escolha uma área específica',
    back: 'Voltar',
    globeHint: 'Arraste para rodar • Desloque para ampliar • Clique num país',
    globeLoading: 'A carregar o globo…',
    loading: 'A carregar o mapa…',
    loadError: 'Não foi possível carregar o mapa. Escolha na lista.',
    useCityInArea: 'Usar {city}, {area}',
    useCity: 'Usar {city}',
    weFoundYou: 'Encontrámo-lo aqui',
    filterPlaceholder: 'Filtrar regiões…',
    noMatches: 'Nenhuma região correspondente',
    mapAriaLabel: 'Mapa de {country}. Escolha uma área',
    subregionAriaLabel: '{name}, {country}',
    listAriaLabel: 'Áreas de {country}',
    selectHint: 'Clique para selecionar',
    pinAriaLabel: 'A sua localização detetada: {label}',
    subregionPromptEyebrow: 'Uma piscadela',
    subregionPromptTitle: 'Parece que está em {name} 👋',
    subregionPromptBody:
      'Podemos mostrar primeiro produtos, preços e lojas de {name}, ou continuar a mostrar tudo de {country}.',
    subregionPromptLocalFirst: '{name} primeiro',
    subregionPromptCountryFirst: 'Mostrar todo o país',
    subregionPromptFootnote: 'Pode mudar de ideias quando quiser.',
    subregionPromptDismiss: 'Fechar',
    subregionPromptAriaLabel: 'Prioridade regional: {name} ou {country}?',
    subregionScopeEyebrow: 'Mais opções',
    subregionScopeTitle: 'A sua escolha: {name}',
    subregionScopeBody:
      'Podemos começar pelos melhores produtos de todo o país, o que lhe dá mais opções, ou colocar os produtos, preços e lojas desta região em primeiro lugar.',
    subregionScopeCountryFirst: 'Ver o melhor do país',
    subregionScopeLocalFirst: '{name} primeiro',
  },
  [LanguageCode.dutch]: {
    chooseAreaTitle: 'Kies je regio in {country}',
    chooseAreaSubtitle:
      'Kies een regio voor lokale prijzen, winkels en levering, of neem het hele land.',
    allOfCountry: 'Heel {country}',
    allOfCountryHint: 'Resultaten uit het hele land',
    orChooseArea: 'of kies een specifieke regio',
    back: 'Terug',
    globeHint: 'Sleep om te draaien • Scroll om te zoomen • Klik op een land',
    globeLoading: 'Wereldbol laden…',
    loading: 'Kaart laden…',
    loadError: 'De kaart kon niet worden geladen. Kies uit de lijst.',
    useCityInArea: '{city}, {area} gebruiken',
    useCity: '{city} gebruiken',
    weFoundYou: 'We hebben je hier gevonden',
    filterPlaceholder: 'Regio’s filteren…',
    noMatches: 'Geen overeenkomende regio',
    mapAriaLabel: 'Kaart van {country}. Kies een regio',
    subregionAriaLabel: '{name}, {country}',
    listAriaLabel: 'Regio’s in {country}',
    selectHint: 'Klik om te selecteren',
    pinAriaLabel: 'Je gedetecteerde locatie: {label}',
    subregionPromptEyebrow: 'Een knipoog',
    subregionPromptTitle: 'Het lijkt erop dat je in {name} bent 👋',
    subregionPromptBody:
      'We kunnen producten, prijzen en winkels uit {name} eerst tonen, of alles uit heel {country} blijven laten zien.',
    subregionPromptLocalFirst: '{name} eerst',
    subregionPromptCountryFirst: 'Heel {country} tonen',
    subregionPromptFootnote: 'Je kunt altijd van gedachten veranderen.',
    subregionPromptDismiss: 'Sluiten',
    subregionPromptAriaLabel: 'Regionale voorkeur: {name} of {country}?',
    subregionScopeEyebrow: 'Meer keuze',
    subregionScopeTitle: 'Uw keuze: {name}',
    subregionScopeBody:
      'We kunnen beginnen met de beste producten uit het hele land, dat geeft u meer keuze, of producten, prijzen en winkels uit {name} eerst tonen.',
    subregionScopeCountryFirst: 'Het beste van het hele land',
    subregionScopeLocalFirst: '{name} eerst',
  },
  [LanguageCode.polish]: {
    chooseAreaTitle: 'Wybierz swój region: {country}',
    chooseAreaSubtitle:
      'Wybierz region, aby zobaczyć lokalne ceny, sklepy i dostawę, albo weź cały kraj.',
    allOfCountry: 'Cały kraj: {country}',
    allOfCountryHint: 'Wyniki z całego kraju',
    orChooseArea: 'albo wybierz konkretny region',
    back: 'Wstecz',
    globeHint: 'Przeciągnij, aby obrócić • Przewiń, aby przybliżyć • Kliknij kraj',
    globeLoading: 'Ładowanie globusa…',
    loading: 'Ładowanie mapy…',
    loadError: 'Nie udało się załadować mapy. Wybierz z listy.',
    useCityInArea: 'Użyj {city}, {area}',
    useCity: 'Użyj {city}',
    weFoundYou: 'Znaleźliśmy Cię tutaj',
    filterPlaceholder: 'Filtruj regiony…',
    noMatches: 'Brak pasującego regionu',
    mapAriaLabel: 'Mapa: {country}. Wybierz region',
    subregionAriaLabel: '{name}, {country}',
    listAriaLabel: 'Regiony: {country}',
    selectHint: 'Kliknij, aby wybrać',
    pinAriaLabel: 'Twoja wykryta lokalizacja: {label}',
    subregionPromptEyebrow: 'Mrugnięcie okiem',
    subregionPromptTitle: 'Wygląda na to, że jesteś w regionie {name} 👋',
    subregionPromptBody:
      'Możemy pokazywać najpierw produkty, ceny i sklepy z regionu {name}, albo nadal pokazywać wszystko z całego kraju: {country}.',
    subregionPromptLocalFirst: 'Najpierw {name}',
    subregionPromptCountryFirst: 'Pokaż cały kraj: {country}',
    subregionPromptFootnote: 'Zawsze możesz zmienić zdanie.',
    subregionPromptDismiss: 'Zamknij',
    subregionPromptAriaLabel: 'Priorytet regionalny: {name} czy {country}?',
    subregionScopeEyebrow: 'Większy wybór',
    subregionScopeTitle: 'Twój wybór: {name}',
    subregionScopeBody:
      'Możemy zacząć od najlepszych produktów z całego kraju, co daje większy wybór, albo najpierw pokazać lokalne produkty, ceny i sklepy.',
    subregionScopeCountryFirst: 'Pokaż najlepsze z całego kraju',
    subregionScopeLocalFirst: 'Najpierw {name}',
  },
  [LanguageCode.swedish]: {
    chooseAreaTitle: 'Välj ditt område i {country}',
    chooseAreaSubtitle:
      'Välj en region för lokala priser, butiker och leverans, eller ta hela landet.',
    allOfCountry: 'Hela {country}',
    allOfCountryHint: 'Resultat från hela landet',
    orChooseArea: 'eller välj ett specifikt område',
    back: 'Tillbaka',
    globeHint: 'Dra för att rotera • Skrolla för att zooma • Klicka på ett land',
    globeLoading: 'Laddar jordgloben…',
    loading: 'Laddar kartan…',
    loadError: 'Kartan kunde inte laddas. Välj från listan i stället.',
    useCityInArea: 'Använd {city}, {area}',
    useCity: 'Använd {city}',
    weFoundYou: 'Vi hittade dig här',
    filterPlaceholder: 'Filtrera regioner…',
    noMatches: 'Ingen matchande region',
    mapAriaLabel: 'Karta över {country}. Välj ett område',
    subregionAriaLabel: '{name}, {country}',
    listAriaLabel: 'Områden i {country}',
    selectHint: 'Klicka för att välja',
    pinAriaLabel: 'Din upptäckta plats: {label}',
    subregionPromptEyebrow: 'En blinkning',
    subregionPromptTitle: 'Det verkar som att du är i {name} 👋',
    subregionPromptBody:
      'Vi kan visa produkter, priser och butiker från {name} först, eller fortsätta visa allt från hela {country}.',
    subregionPromptLocalFirst: '{name} först',
    subregionPromptCountryFirst: 'Visa hela {country}',
    subregionPromptFootnote: 'Du kan ändra dig när som helst.',
    subregionPromptDismiss: 'Stäng',
    subregionPromptAriaLabel: 'Regional prioritet: {name} eller {country}?',
    subregionScopeEyebrow: 'Fler att välja bland',
    subregionScopeTitle: 'Ditt val: {name}',
    subregionScopeBody:
      'Vi kan börja med de bästa produkterna från hela landet, vilket ger dig fler att välja bland, eller visa produkter, priser och butiker från {name} först.',
    subregionScopeCountryFirst: 'Visa det bästa från hela landet',
    subregionScopeLocalFirst: '{name} först',
  },
  [LanguageCode.welsh]: {
    chooseAreaTitle: 'Dewiswch eich ardal yn {country}',
    chooseAreaSubtitle:
      'Dewiswch ranbarth ar gyfer prisiau, siopau a danfoniadau lleol, neu cymerwch y wlad gyfan.',
    allOfCountry: '{country} gyfan',
    allOfCountryHint: "Canlyniadau o bob rhan o'r wlad",
    orChooseArea: 'neu dewiswch ardal benodol',
    back: 'Yn ôl',
    globeHint: 'Llusgwch i droi • Sgroliwch i chwyddo • Cliciwch ar wlad',
    globeLoading: 'Wrthi\'n llwytho\'r glôb…',
    loading: "Wrthi'n llwytho'r map…",
    loadError: "Methu llwytho'r map. Dewiswch o'r rhestr.",
    useCityInArea: 'Defnyddio {city}, {area}',
    useCity: 'Defnyddio {city}',
    weFoundYou: 'Fe wnaethon ni eich canfod yma',
    filterPlaceholder: 'Hidlo rhanbarthau…',
    noMatches: 'Dim rhanbarth cyfatebol',
    mapAriaLabel: 'Map o {country}. Dewiswch ardal',
    subregionAriaLabel: '{name}, {country}',
    listAriaLabel: 'Ardaloedd yn {country}',
    selectHint: 'Cliciwch i ddewis',
    pinAriaLabel: 'Eich lleoliad canfyddedig: {label}',
    subregionPromptEyebrow: 'Winc fach',
    subregionPromptTitle: "Mae'n edrych fel eich bod yn {name} 👋",
    subregionPromptBody:
      'Gallwn ddangos cynhyrchion, prisiau a siopau {name} yn gyntaf, neu ddal ati i ddangos popeth o bob rhan o {country}.',
    subregionPromptLocalFirst: '{name} yn gyntaf',
    subregionPromptCountryFirst: 'Dangos {country} gyfan',
    subregionPromptFootnote: 'Gallwch newid eich meddwl unrhyw bryd.',
    subregionPromptDismiss: 'Cau',
    subregionPromptAriaLabel: 'Blaenoriaeth ranbarthol: {name} neu {country}?',
    subregionScopeEyebrow: 'Mwy o ddewis',
    subregionScopeTitle: 'Eich dewis: {name}',
    subregionScopeBody:
      "Gallwn ddechrau gyda'r cynhyrchion gorau o'r wlad gyfan, sy'n rhoi mwy o ddewis i chi, neu ddangos cynhyrchion, prisiau a siopau {name} yn gyntaf.",
    subregionScopeCountryFirst: "Dangos y gorau o'r wlad",
    subregionScopeLocalFirst: '{name} yn gyntaf',
  },
  [LanguageCode.irish]: {
    chooseAreaTitle: 'Roghnaigh do cheantar in {country}',
    chooseAreaSubtitle:
      'Roghnaigh réigiún le haghaidh praghsanna, siopaí agus seachadadh áitiúil, nó tóg an tír ar fad.',
    allOfCountry: '{country} ar fad',
    allOfCountryHint: 'Torthaí ón tír ar fad',
    orChooseArea: 'nó roghnaigh ceantar ar leith',
    back: 'Ar ais',
    globeHint: 'Tarraing chun rothlú • Scrollaigh chun súmáil • Cliceáil ar thír',
    globeLoading: 'An cruinneog á lódáil…',
    loading: 'An léarscáil á lódáil…',
    loadError: 'Níorbh fhéidir an léarscáil a lódáil. Roghnaigh ón liosta.',
    useCityInArea: 'Úsáid {city}, {area}',
    useCity: 'Úsáid {city}',
    weFoundYou: 'Fuaireamar anseo thú',
    filterPlaceholder: 'Scag réigiúin…',
    noMatches: 'Níl aon réigiún comhoiriúnach ann',
    mapAriaLabel: 'Léarscáil de {country}. Roghnaigh ceantar',
    subregionAriaLabel: '{name}, {country}',
    listAriaLabel: 'Ceantair in {country}',
    selectHint: 'Cliceáil chun roghnú',
    pinAriaLabel: 'An suíomh a braitheadh: {label}',
    subregionPromptEyebrow: 'Caochadh súile',
    subregionPromptTitle: 'Dealraíonn sé go bhfuil tú i {name} 👋',
    subregionPromptBody:
      'Is féidir linn táirgí, praghsanna agus siopaí {name} a thaispeáint ar dtús, nó leanúint ar aghaidh ag taispeáint gach rud ó {country}.',
    subregionPromptLocalFirst: '{name} ar dtús',
    subregionPromptCountryFirst: 'Taispeáin {country} ar fad',
    subregionPromptFootnote: "Is féidir leat d'intinn a athrú am ar bith.",
    subregionPromptDismiss: 'Dún',
    subregionPromptAriaLabel: 'Tosaíocht réigiúnach: {name} nó {country}?',
    subregionScopeEyebrow: 'Tuilleadh le roghnú',
    subregionScopeTitle: 'Do rogha: {name}',
    subregionScopeBody:
      'Is féidir linn tosú leis na táirgí is fearr ar fud na tíre, rud a thugann tuilleadh roghanna duit, nó táirgí, praghsanna agus siopaí {name} a chur ar dtús.',
    subregionScopeCountryFirst: 'An chuid is fearr den tír',
    subregionScopeLocalFirst: '{name} ar dtús',
  },
  [LanguageCode.scottishGaelic]: {
    chooseAreaTitle: 'Tagh do sgìre ann an {country}',
    chooseAreaSubtitle:
      'Tagh roinn airson prìsean, bùithtean is lìbhrigeadh ionadail, no gabh an dùthaich gu lèir.',
    allOfCountry: '{country} gu lèir',
    allOfCountryHint: 'Toraidhean bhon dùthaich gu lèir',
    orChooseArea: 'no tagh sgìre shònraichte',
    back: 'Air ais',
    globeHint: 'Slaod gus cuairteachadh • Sgrolaich gus sùmadh • Briog air dùthaich',
    globeLoading: 'A\' luchdachadh a\' chruinne…',
    loading: "A' luchdachadh a' mhapa…",
    loadError: "Cha b' urrainn dhuinn am mapa a luchdachadh. Tagh bhon liosta.",
    useCityInArea: 'Cleachd {city}, {area}',
    useCity: 'Cleachd {city}',
    weFoundYou: 'Lorg sinn thu an seo',
    filterPlaceholder: 'Criathraich roinnean…',
    noMatches: "Chan eil roinn a' freagairt",
    mapAriaLabel: 'Mapa de {country}. Tagh sgìre',
    subregionAriaLabel: '{name}, {country}',
    listAriaLabel: 'Sgìrean ann an {country}',
    selectHint: 'Briog gus taghadh',
    pinAriaLabel: 'An t-àite a chaidh a lorg: {label}',
    subregionPromptEyebrow: 'Priobadh sùla',
    subregionPromptTitle: 'Tha coltas gu bheil thu ann an {name} 👋',
    subregionPromptBody:
      "Is urrainn dhuinn bathar, prìsean is bùithtean {name} a shealltainn an toiseach, no cumail a' sealltainn a h-uile càil bho {country}.",
    subregionPromptLocalFirst: '{name} an toiseach',
    subregionPromptCountryFirst: 'Seall {country} gu lèir',
    subregionPromptFootnote: "Faodaidh tu d' inntinn atharrachadh uair sam bith.",
    subregionPromptDismiss: 'Dùin',
    subregionPromptAriaLabel: 'Prìomhachas roinneil: {name} no {country}?',
    subregionScopeEyebrow: 'Barrachd roghainn',
    subregionScopeTitle: 'Do roghainn: {name}',
    subregionScopeBody:
      'Faodaidh sinn tòiseachadh leis na toraidhean as fheàrr bhon dùthaich air fad, a bheir barrachd roghainn dhut, no toraidhean, prìsean is bùthan {name} a shealltainn an toiseach.',
    subregionScopeCountryFirst: 'Na tha as fheàrr air feadh na dùthcha',
    subregionScopeLocalFirst: '{name} an toiseach',
  },
  [LanguageCode.hindi]: {
    chooseAreaTitle: '{country} में अपना क्षेत्र चुनें',
    chooseAreaSubtitle:
      'स्थानीय कीमतों, स्टोर और डिलीवरी के लिए कोई क्षेत्र चुनें, या पूरा देश चुनें।',
    allOfCountry: 'पूरा {country}',
    allOfCountryHint: 'पूरे देश के परिणाम',
    orChooseArea: 'या कोई विशेष क्षेत्र चुनें',
    back: 'वापस',
    globeHint: 'घुमाने के लिए खींचें • ज़ूम के लिए स्क्रॉल करें • देश पर क्लिक करें',
    globeLoading: 'ग्लोब लोड हो रहा है…',
    loading: 'मानचित्र लोड हो रहा है…',
    loadError: 'मानचित्र लोड नहीं हो सका। सूची में से चुनें।',
    useCityInArea: '{city}, {area} चुनें',
    useCity: '{city} चुनें',
    weFoundYou: 'हमने आपको यहाँ पाया',
    filterPlaceholder: 'क्षेत्र फ़िल्टर करें…',
    noMatches: 'कोई मेल खाता क्षेत्र नहीं',
    mapAriaLabel: '{country} का मानचित्र। कोई क्षेत्र चुनें',
    subregionAriaLabel: '{name}, {country}',
    listAriaLabel: '{country} के क्षेत्र',
    selectHint: 'चुनने के लिए क्लिक करें',
    pinAriaLabel: 'आपका पता लगाया गया स्थान: {label}',
    subregionPromptEyebrow: 'हमारी ओर से एक इशारा',
    subregionPromptTitle: 'लगता है आप {name} में हैं 👋',
    subregionPromptBody:
      'हम {name} के उत्पाद, कीमतें और स्टोर पहले दिखा सकते हैं, या आपको पूरे {country} से सब कुछ दिखाते रह सकते हैं।',
    subregionPromptLocalFirst: 'पहले {name}',
    subregionPromptCountryFirst: 'पूरा {country} दिखाएँ',
    subregionPromptFootnote: 'आप कभी भी अपना मन बदल सकते हैं।',
    subregionPromptDismiss: 'बंद करें',
    subregionPromptAriaLabel: 'क्षेत्रीय प्राथमिकता: {name} या {country}?',
    subregionScopeEyebrow: 'और ज़्यादा विकल्प',
    subregionScopeTitle: 'आपकी पसंद: {name}',
    subregionScopeBody:
      'हम पूरे देश के सबसे अच्छे उत्पाद पहले दिखा सकते हैं, जिससे आपको ज़्यादा विकल्प मिलते हैं, या {name} के उत्पाद, कीमतें और स्टोर पहले रख सकते हैं।',
    subregionScopeCountryFirst: 'पूरे देश का सबसे अच्छा दिखाएँ',
    subregionScopeLocalFirst: '{name} को पहले रखें',
  },
  [LanguageCode.catalan]: {
    chooseAreaTitle: 'Tria la teva zona a {country}',
    chooseAreaSubtitle:
      "Tria una regió per a preus, botigues i lliuraments locals, o queda't amb tot el país.",
    allOfCountry: 'Tot {country}',
    allOfCountryHint: 'Resultats de tot el país',
    orChooseArea: 'o tria una zona concreta',
    back: 'Enrere',
    globeHint: 'Arrossega per girar • Desplaça per apropar • Fes clic en un país',
    globeLoading: 'S\'està carregant el globus…',
    loading: "S'està carregant el mapa…",
    loadError: "No s'ha pogut carregar el mapa. Tria de la llista.",
    useCityInArea: 'Utilitza {city}, {area}',
    useCity: 'Utilitza {city}',
    weFoundYou: "T'hem trobat aquí",
    filterPlaceholder: 'Filtra les regions…',
    noMatches: 'Cap regió coincident',
    mapAriaLabel: 'Mapa de {country}. Tria una zona',
    subregionAriaLabel: '{name}, {country}',
    listAriaLabel: 'Zones de {country}',
    selectHint: 'Fes clic per seleccionar',
    pinAriaLabel: 'La teva ubicació detectada: {label}',
    subregionPromptEyebrow: "Una picada d'ullet",
    subregionPromptTitle: 'Sembla que ets a {name} 👋',
    subregionPromptBody:
      'Podem mostrar primer els productes, preus i botigues de {name}, o seguir mostrant-te tot el de {country}.',
    subregionPromptLocalFirst: 'Primer {name}',
    subregionPromptCountryFirst: 'Mostra tot {country}',
    subregionPromptFootnote: "Pots canviar d'opinió quan vulguis.",
    subregionPromptDismiss: 'Tanca',
    subregionPromptAriaLabel: 'Prioritat regional: {name} o {country}?',
    subregionScopeEyebrow: 'Més opcions',
    subregionScopeTitle: 'La teva tria: {name}',
    subregionScopeBody:
      "Podem començar pels millors productes de tot el país, cosa que et dóna més opcions, o posar primer els productes, preus i botigues d'aquesta zona.",
    subregionScopeCountryFirst: 'El millor de tot el país',
    subregionScopeLocalFirst: 'Primer {name}',
  },
  [LanguageCode.galician]: {
    chooseAreaTitle: 'Escolle a túa zona en {country}',
    chooseAreaSubtitle:
      'Escolle unha rexión para prezos, tendas e entregas locais, ou queda con todo o país.',
    allOfCountry: 'Todo {country}',
    allOfCountryHint: 'Resultados de todo o país',
    orChooseArea: 'ou escolle unha zona concreta',
    back: 'Volver',
    globeHint: 'Arrastra para xirar • Desprázate para achegar • Fai clic nun país',
    globeLoading: 'Cargando o globo…',
    loading: 'Cargando o mapa…',
    loadError: 'Non se puido cargar o mapa. Escolle da lista.',
    useCityInArea: 'Usar {city}, {area}',
    useCity: 'Usar {city}',
    weFoundYou: 'Atopámoste aquí',
    filterPlaceholder: 'Filtrar rexións…',
    noMatches: 'Ningunha rexión coincidente',
    mapAriaLabel: 'Mapa de {country}. Escolle unha zona',
    subregionAriaLabel: '{name}, {country}',
    listAriaLabel: 'Zonas de {country}',
    selectHint: 'Fai clic para seleccionar',
    pinAriaLabel: 'A túa localización detectada: {label}',
    subregionPromptEyebrow: 'Un chisco de ollo',
    subregionPromptTitle: 'Parece que estás en {name} 👋',
    subregionPromptBody:
      'Podemos amosar primeiro os produtos, prezos e tendas de {name}, ou seguir amosándoche todo o de {country}.',
    subregionPromptLocalFirst: 'Primeiro {name}',
    subregionPromptCountryFirst: 'Amosar todo {country}',
    subregionPromptFootnote: 'Podes cambiar de opinión cando queiras.',
    subregionPromptDismiss: 'Pechar',
    subregionPromptAriaLabel: 'Prioridade rexional: {name} ou {country}?',
    subregionScopeEyebrow: 'Máis para escoller',
    subregionScopeTitle: 'A túa escolla: {name}',
    subregionScopeBody:
      'Podemos comezar polos mellores produtos de todo o país, o que che dá máis opcións, ou poñer primeiro os produtos, prezos e tendas desta zona.',
    subregionScopeCountryFirst: 'O mellor de todo o país',
    subregionScopeLocalFirst: 'Primeiro {name}',
  },
  [LanguageCode.basque]: {
    chooseAreaTitle: '{country}: aukeratu zure eremua',
    chooseAreaSubtitle:
      'Aukeratu eskualde bat tokiko prezioak, dendak eta banaketa ikusteko, edo hartu herrialde osoa.',
    allOfCountry: '{country} osoa',
    allOfCountryHint: 'Herrialde osoko emaitzak',
    orChooseArea: 'edo aukeratu eremu jakin bat',
    back: 'Atzera',
    globeHint: 'Arrastatu biratzeko • Korritu zooma egiteko • Egin klik herrialde batean',
    globeLoading: 'Globoa kargatzen…',
    loading: 'Mapa kargatzen…',
    loadError: 'Ezin izan da mapa kargatu. Aukeratu zerrendatik.',
    useCityInArea: 'Erabili {city}, {area}',
    useCity: 'Erabili {city}',
    weFoundYou: 'Hemen aurkitu zaitugu',
    filterPlaceholder: 'Iragazi eskualdeak…',
    noMatches: 'Ez dago bat datorren eskualderik',
    mapAriaLabel: '{country} mapa. Aukeratu eremu bat',
    subregionAriaLabel: '{name}, {country}',
    listAriaLabel: '{country}: eremuak',
    selectHint: 'Egin klik hautatzeko',
    pinAriaLabel: 'Antzemandako kokapena: {label}',
    subregionPromptEyebrow: 'Keinu bat',
    subregionPromptTitle: 'Badirudi {name}n zaudela 👋',
    subregionPromptBody:
      '{name}ko produktuak, prezioak eta dendak lehenengo erakutsi ditzakegu, edo {country} osoko guztia erakusten jarraitu.',
    subregionPromptLocalFirst: '{name} lehenengo',
    subregionPromptCountryFirst: 'Erakutsi {country} osoa',
    subregionPromptFootnote: 'Noiznahi alda dezakezu iritzia.',
    subregionPromptDismiss: 'Itxi',
    subregionPromptAriaLabel: 'Eskualde-lehentasuna: {name} ala {country}?',
    subregionScopeEyebrow: 'Aukera gehiago',
    subregionScopeTitle: 'Zure hautua: {name}',
    subregionScopeBody:
      'Herrialde osoko produkturik onenekin has gaitezke, horrek aukera gehiago ematen dizu, edo {name} produktuak, prezioak eta dendak lehenengo erakutsi ditzakegu.',
    subregionScopeCountryFirst: 'Herrialde osoko onena',
    subregionScopeLocalFirst: '{name} lehenengo',
  },
  [LanguageCode.romansh]: {
    chooseAreaTitle: 'Tscherni tia regiun en {country}',
    chooseAreaSubtitle:
      "Tscherni ina regiun per pretschs, butias e furniziuns localas, u prendi l'entir pajais.",
    allOfCountry: 'Tut {country}',
    allOfCountryHint: "Resultats da l'entir pajais",
    orChooseArea: 'u tscherni ina regiun specifica',
    back: 'Enavos',
    globeHint: 'Trair per rotar • Rullar per zoomar • Cliccar sin in pajais',
    globeLoading: 'Chargiar il globus…',
    loading: 'Chargiar la charta…',
    loadError: "La charta n'ha betg pudì vegnir chargiada. Tscherni da la glista.",
    useCityInArea: 'Duvrar {city}, {area}',
    useCity: 'Duvrar {city}',
    weFoundYou: "Nus t'avain chattà qua",
    filterPlaceholder: 'Filtrar las regiuns…',
    noMatches: 'Nagina regiun correspundenta',
    mapAriaLabel: 'Charta da {country}. Tscherni ina regiun',
    subregionAriaLabel: '{name}, {country}',
    listAriaLabel: 'Regiuns en {country}',
    selectHint: 'Cliccar per tscherner',
    pinAriaLabel: 'Tia posiziun chattada: {label}',
    subregionPromptEyebrow: "In pign zim da l'egl",
    subregionPromptTitle: "I para ch'i ties en {name} 👋",
    subregionPromptBody:
      "Nus pudain mussar l'emprim ils products, ils pretschs e las butias da {name}, u cuntinuar a mussar tut da {country}.",
    subregionPromptLocalFirst: "L'emprim {name}",
    subregionPromptCountryFirst: 'Mussar tut {country}',
    subregionPromptFootnote: 'Ti pos midar tia opiniun mintga mument.',
    subregionPromptDismiss: 'Serrar',
    subregionPromptAriaLabel: 'Prioritad regiunala: {name} u {country}?',
    subregionScopeEyebrow: 'Dapli tscherna',
    subregionScopeTitle: 'Vossa tscherna: {name}',
    subregionScopeBody:
      "Nus pudain cumenzar cun ils meglers products da tut il pajais, quai As dat dapli tscherna, u mussar l'emprim products, pretschs e butias da {name}.",
    subregionScopeCountryFirst: 'Il meglier da tut il pajais',
    subregionScopeLocalFirst: "{name} l'emprim",
  },
  [LanguageCode.maori]: {
    chooseAreaTitle: 'Tīpakohia tō rohe i {country}',
    chooseAreaSubtitle:
      'Kōwhiria he rohe mō ngā utu, toa me te tuku ā-rohe, ka rānei tangohia te whenua katoa.',
    allOfCountry: '{country} katoa',
    allOfCountryHint: 'Ngā hua puta noa i te whenua',
    orChooseArea: 'ka rānei kōwhiria tētahi rohe motuhake',
    back: 'Hoki',
    globeHint: 'Tōia kia hurihuri • Panuku kia whakarahi • Pāwhiritia he whenua',
    globeLoading: 'Kei te uta i te ao…',
    loading: 'Kei te uta i te mahere…',
    loadError: 'Kāore i taea te uta i te mahere. Kōwhiria mai i te rārangi.',
    useCityInArea: 'Whakamahia {city}, {area}',
    useCity: 'Whakamahia {city}',
    weFoundYou: 'I kitea koe ki konei',
    filterPlaceholder: 'Tātari rohe…',
    noMatches: 'Kāore he rohe e ōrite ana',
    mapAriaLabel: 'Mahere o {country}. Kōwhiria he rohe',
    subregionAriaLabel: '{name}, {country}',
    listAriaLabel: 'Ngā rohe o {country}',
    selectHint: 'Pāwhiritia hei tīpako',
    pinAriaLabel: 'Tō wāhi i kitea: {label}',
    subregionPromptEyebrow: 'He kamo nā mātou',
    subregionPromptTitle: 'Te āhua nei kei {name} koe 👋',
    subregionPromptBody:
      'Ka taea e mātou te whakaatu i ngā hua, utu me ngā toa o {name} i te tuatahi, ka rānei te whakaatu tonu i ngā mea katoa mai i {country}.',
    subregionPromptLocalFirst: 'Ko {name} tuatahi',
    subregionPromptCountryFirst: 'Whakaatu i {country} katoa',
    subregionPromptFootnote: 'Ka taea e koe te huri whakaaro i ngā wā katoa.',
    subregionPromptDismiss: 'Katia',
    subregionPromptAriaLabel: 'Whakaarotau ā-rohe: {name}, {country} rānei?',
    subregionScopeEyebrow: 'He maha ake ngā kōwhiringa',
    subregionScopeTitle: 'Tō kōwhiringa: {name}',
    subregionScopeBody:
      'Ka taea te tīmata ki ngā hua pai rawa o te motu whānui, kia maha ake ai ō kōwhiringa, ka taea rānei te whakaatu tuatahi i ngā hua, ngā utu me ngā toa o {name}.',
    subregionScopeCountryFirst: 'Ngā mea pai rawa o te motu',
    subregionScopeLocalFirst: 'Ko {name} tuatahi',
  },
  [LanguageCode.tamil]: {
    chooseAreaTitle: '{country} இல் உங்கள் பகுதியைத் தேர்ந்தெடுக்கவும்',
    chooseAreaSubtitle:
      'உள்ளூர் விலைகள், கடைகள் மற்றும் விநியோகத்திற்கு ஒரு பகுதியைத் தேர்வுசெய்யவும், அல்லது நாடு முழுவதையும் எடுத்துக்கொள்ளவும்.',
    allOfCountry: '{country} முழுவதும்',
    allOfCountryHint: 'நாடு முழுவதற்குமான முடிவுகள்',
    orChooseArea: 'அல்லது ஒரு குறிப்பிட்ட பகுதியைத் தேர்வுசெய்யவும்',
    back: 'பின்செல்',
    globeHint: 'சுழற்ற இழுக்கவும் • பெரிதாக்க உருட்டவும் • ஒரு நாட்டைக் கிளிக் செய்யவும்',
    globeLoading: 'உலகக் கோளம் ஏற்றப்படுகிறது…',
    loading: 'வரைபடம் ஏற்றப்படுகிறது…',
    loadError: 'வரைபடத்தை ஏற்ற முடியவில்லை. பட்டியலிலிருந்து தேர்வுசெய்யவும்.',
    useCityInArea: '{city}, {area} ஐப் பயன்படுத்து',
    useCity: '{city} ஐப் பயன்படுத்து',
    weFoundYou: 'உங்களை இங்கே கண்டோம்',
    filterPlaceholder: 'பகுதிகளை வடிகட்டு…',
    noMatches: 'பொருந்தும் பகுதி இல்லை',
    mapAriaLabel: '{country} வரைபடம். ஒரு பகுதியைத் தேர்வுசெய்யவும்',
    subregionAriaLabel: '{name}, {country}',
    listAriaLabel: '{country} இன் பகுதிகள்',
    selectHint: 'தேர்வுசெய்ய கிளிக் செய்யவும்',
    pinAriaLabel: 'கண்டறியப்பட்ட உங்கள் இருப்பிடம்: {label}',
    subregionPromptEyebrow: 'எங்களிடமிருந்து ஒரு சிமிட்டல்',
    subregionPromptTitle: 'நீங்கள் {name} இல் இருப்பது போல் தெரிகிறது 👋',
    subregionPromptBody:
      '{name} இன் தயாரிப்புகள், விலைகள் மற்றும் கடைகளை முதலில் காட்டலாம், அல்லது {country} முழுவதிலிருந்தும் அனைத்தையும் தொடர்ந்து காட்டலாம்.',
    subregionPromptLocalFirst: 'முதலில் {name}',
    subregionPromptCountryFirst: '{country} முழுவதையும் காட்டு',
    subregionPromptFootnote: 'நீங்கள் எப்போது வேண்டுமானாலும் மனம் மாற்றலாம்.',
    subregionPromptDismiss: 'மூடு',
    subregionPromptAriaLabel: 'பிராந்திய முன்னுரிமை: {name} அல்லது {country}?',
    subregionScopeEyebrow: 'இன்னும் அதிக தேர்வுகள்',
    subregionScopeTitle: 'உங்கள் தேர்வு: {name}',
    subregionScopeBody:
      'நாடு முழுவதிலும் உள்ள சிறந்த பொருட்களை முதலில் காட்டலாம், இதனால் அதிக தேர்வுகள் கிடைக்கும், அல்லது {name} பொருட்கள், விலைகள் மற்றும் கடைகளை முதலில் வைக்கலாம்.',
    subregionScopeCountryFirst: 'நாடு முழுவதும் சிறந்தவை',
    subregionScopeLocalFirst: '{name} முதலில்',
  },
  [LanguageCode.telugu]: {
    chooseAreaTitle: '{country} లో మీ ప్రాంతాన్ని ఎంచుకోండి',
    chooseAreaSubtitle:
      'స్థానిక ధరలు, దుకాణాలు మరియు డెలివరీ కోసం ఒక ప్రాంతాన్ని ఎంచుకోండి, లేదా మొత్తం దేశాన్ని తీసుకోండి.',
    allOfCountry: 'మొత్తం {country}',
    allOfCountryHint: 'దేశవ్యాప్త ఫలితాలు',
    orChooseArea: 'లేదా ఒక నిర్దిష్ట ప్రాంతాన్ని ఎంచుకోండి',
    back: 'వెనుకకు',
    globeHint: 'తిప్పడానికి లాగండి • జూమ్ కోసం స్క్రోల్ చేయండి • ఒక దేశంపై క్లిక్ చేయండి',
    globeLoading: 'గ్లోబ్ లోడ్ అవుతోంది…',
    loading: 'మ్యాప్ లోడ్ అవుతోంది…',
    loadError: 'మ్యాప్‌ను లోడ్ చేయడం సాధ్యం కాలేదు. జాబితా నుండి ఎంచుకోండి.',
    useCityInArea: '{city}, {area} ఉపయోగించు',
    useCity: '{city} ఉపయోగించు',
    weFoundYou: 'మిమ్మల్ని ఇక్కడ కనుగొన్నాము',
    filterPlaceholder: 'ప్రాంతాలను ఫిల్టర్ చేయి…',
    noMatches: 'సరిపోలే ప్రాంతం లేదు',
    mapAriaLabel: '{country} మ్యాప్. ఒక ప్రాంతాన్ని ఎంచుకోండి',
    subregionAriaLabel: '{name}, {country}',
    listAriaLabel: '{country} లోని ప్రాంతాలు',
    selectHint: 'ఎంచుకోవడానికి క్లిక్ చేయండి',
    pinAriaLabel: 'గుర్తించిన మీ స్థానం: {label}',
    subregionPromptEyebrow: 'మా నుండి ఒక సైగ',
    subregionPromptTitle: 'మీరు {name} లో ఉన్నట్టు కనిపిస్తోంది 👋',
    subregionPromptBody:
      '{name} ఉత్పత్తులు, ధరలు మరియు దుకాణాలను ముందుగా చూపించగలము, లేదా {country} మొత్తం నుండి అన్నింటినీ చూపిస్తూనే ఉండగలము.',
    subregionPromptLocalFirst: 'ముందుగా {name}',
    subregionPromptCountryFirst: 'మొత్తం {country} చూపించు',
    subregionPromptFootnote: 'మీరు ఎప్పుడైనా మీ నిర్ణయం మార్చుకోవచ్చు.',
    subregionPromptDismiss: 'మూసివేయి',
    subregionPromptAriaLabel: 'ప్రాంతీయ ప్రాధాన్యత: {name} లేదా {country}?',
    subregionScopeEyebrow: 'మరిన్ని ఎంపికలు',
    subregionScopeTitle: 'మీ ఎంపిక: {name}',
    subregionScopeBody:
      'దేశం మొత్తంలోని ఉత్తమ ఉత్పత్తులను ముందుగా చూపించగలం, దీనివల్ల మీకు ఎక్కువ ఎంపికలు ఉంటాయి, లేదా {name} ఉత్పత్తులు, ధరలు, దుకాణాలను ముందుగా ఉంచగలం.',
    subregionScopeCountryFirst: 'దేశం మొత్తంలో ఉత్తమమైనవి',
    subregionScopeLocalFirst: '{name} ముందుగా',
  },
  [LanguageCode.bengali]: {
    chooseAreaTitle: '{country}-এ আপনার এলাকা বেছে নিন',
    chooseAreaSubtitle:
      'স্থানীয় দাম, দোকান ও ডেলিভারির জন্য একটি অঞ্চল বেছে নিন, অথবা পুরো দেশ নিন।',
    allOfCountry: 'সমগ্র {country}',
    allOfCountryHint: 'সারা দেশের ফলাফল',
    orChooseArea: 'অথবা একটি নির্দিষ্ট এলাকা বেছে নিন',
    back: 'পিছনে',
    globeHint: 'ঘোরাতে টানুন • জুম করতে স্ক্রোল করুন • একটি দেশে ক্লিক করুন',
    globeLoading: 'গ্লোব লোড হচ্ছে…',
    loading: 'মানচিত্র লোড হচ্ছে…',
    loadError: 'মানচিত্র লোড করা যায়নি। তালিকা থেকে বেছে নিন।',
    useCityInArea: '{city}, {area} ব্যবহার করুন',
    useCity: '{city} ব্যবহার করুন',
    weFoundYou: 'আমরা আপনাকে এখানে পেয়েছি',
    filterPlaceholder: 'অঞ্চল ফিল্টার করুন…',
    noMatches: 'কোনও মিল থাকা অঞ্চল নেই',
    mapAriaLabel: '{country}-এর মানচিত্র। একটি এলাকা বেছে নিন',
    subregionAriaLabel: '{name}, {country}',
    listAriaLabel: '{country}-এর এলাকাসমূহ',
    selectHint: 'নির্বাচন করতে ক্লিক করুন',
    pinAriaLabel: 'শনাক্ত করা আপনার অবস্থান: {label}',
    subregionPromptEyebrow: 'আমাদের একটি চোখের ইশারা',
    subregionPromptTitle: 'মনে হচ্ছে আপনি {name}-এ আছেন 👋',
    subregionPromptBody:
      'আমরা {name}-এর পণ্য, দাম ও দোকান আগে দেখাতে পারি, অথবা সমগ্র {country} থেকে সবকিছু দেখাতে থাকতে পারি।',
    subregionPromptLocalFirst: 'আগে {name}',
    subregionPromptCountryFirst: 'সমগ্র {country} দেখান',
    subregionPromptFootnote: 'আপনি যেকোনও সময় মত বদলাতে পারেন।',
    subregionPromptDismiss: 'বন্ধ করুন',
    subregionPromptAriaLabel: 'আঞ্চলিক অগ্রাধিকার: {name} নাকি {country}?',
    subregionScopeEyebrow: 'আরও বেশি বিকল্প',
    subregionScopeTitle: 'আপনার পছন্দ: {name}',
    subregionScopeBody:
      'আমরা সারা দেশের সেরা পণ্য আগে দেখাতে পারি, যাতে আপনি আরও বেশি বিকল্প পান, অথবা {name} এর পণ্য, দাম ও দোকান আগে রাখতে পারি।',
    subregionScopeCountryFirst: 'সারা দেশের সেরা দেখান',
    subregionScopeLocalFirst: '{name} আগে',
  },
  [LanguageCode.marathi]: {
    chooseAreaTitle: '{country} मधील तुमचा भाग निवडा',
    chooseAreaSubtitle:
      'स्थानिक किमती, दुकाने आणि वितरणासाठी एक प्रदेश निवडा, किंवा संपूर्ण देश घ्या.',
    allOfCountry: 'संपूर्ण {country}',
    allOfCountryHint: 'देशभरातील निकाल',
    orChooseArea: 'किंवा एक विशिष्ट भाग निवडा',
    back: 'मागे',
    globeHint: 'फिरवण्यासाठी ओढा • झूमसाठी स्क्रोल करा • एका देशावर क्लिक करा',
    globeLoading: 'ग्लोब लोड होत आहे…',
    loading: 'नकाशा लोड होत आहे…',
    loadError: 'नकाशा लोड करता आला नाही. यादीतून निवडा.',
    useCityInArea: '{city}, {area} वापरा',
    useCity: '{city} वापरा',
    weFoundYou: 'आम्ही तुम्हाला येथे शोधले',
    filterPlaceholder: 'प्रदेश फिल्टर करा…',
    noMatches: 'जुळणारा प्रदेश नाही',
    mapAriaLabel: '{country} चा नकाशा. एक भाग निवडा',
    subregionAriaLabel: '{name}, {country}',
    listAriaLabel: '{country} मधील भाग',
    selectHint: 'निवडण्यासाठी क्लिक करा',
    pinAriaLabel: 'तुमचे शोधलेले स्थान: {label}',
    subregionPromptEyebrow: 'आमच्याकडून एक डोळा मिचकावणे',
    subregionPromptTitle: 'असे दिसते की तुम्ही {name} मध्ये आहात 👋',
    subregionPromptBody:
      'आम्ही {name} ची उत्पादने, किमती आणि दुकाने प्रथम दाखवू शकतो, किंवा संपूर्ण {country} मधील सर्व काही दाखवत राहू शकतो.',
    subregionPromptLocalFirst: 'प्रथम {name}',
    subregionPromptCountryFirst: 'संपूर्ण {country} दाखवा',
    subregionPromptFootnote: 'तुम्ही कधीही तुमचा विचार बदलू शकता.',
    subregionPromptDismiss: 'बंद करा',
    subregionPromptAriaLabel: 'प्रादेशिक प्राधान्य: {name} की {country}?',
    subregionScopeEyebrow: 'अधिक पर्याय',
    subregionScopeTitle: 'तुमची निवड: {name}',
    subregionScopeBody:
      'आम्ही संपूर्ण देशातील सर्वोत्तम उत्पादने आधी दाखवू शकतो, त्यामुळे तुम्हाला अधिक पर्याय मिळतात, किंवा {name} मधील उत्पादने, किमती आणि दुकाने आधी ठेवू शकतो.',
    subregionScopeCountryFirst: 'संपूर्ण देशातील सर्वोत्तम दाखवा',
    subregionScopeLocalFirst: '{name} आधी',
  },
  [LanguageCode.japanese]: {
    chooseAreaTitle: '{country}のエリアを選択',
    chooseAreaSubtitle:
      '地域を選ぶと、その地域の価格・店舗・配送が表示されます。国全体を選ぶこともできます。',
    allOfCountry: '{country}全体',
    allOfCountryHint: '全国の検索結果',
    orChooseArea: 'または特定のエリアを選択',
    back: '戻る',
    globeHint: 'ドラッグで回転 • スクロールで拡大 • 国をクリックして選択',
    globeLoading: '地球儀を読み込んでいます…',
    loading: '地図を読み込んでいます…',
    loadError: '地図を読み込めませんでした。リストから選択してください。',
    useCityInArea: '{city}、{area}を使用',
    useCity: '{city}を使用',
    weFoundYou: 'ここにいると判定しました',
    filterPlaceholder: '地域を絞り込む…',
    noMatches: '一致する地域がありません',
    mapAriaLabel: '{country}の地図。エリアを選択',
    subregionAriaLabel: '{name}、{country}',
    listAriaLabel: '{country}のエリア',
    selectHint: 'クリックして選択',
    pinAriaLabel: '検出された現在地: {label}',
    subregionPromptEyebrow: 'ちょっとしたご提案',
    subregionPromptTitle: '{name}にいらっしゃるようですね 👋',
    subregionPromptBody:
      '{name}の商品・価格・店舗を優先して表示することも、{country}全体から引き続き表示することもできます。',
    subregionPromptLocalFirst: '{name}を優先',
    subregionPromptCountryFirst: '{country}全体を表示',
    subregionPromptFootnote: 'いつでも変更できます。',
    subregionPromptDismiss: '閉じる',
    subregionPromptAriaLabel: '地域の優先設定: {name}か{country}か',
    subregionScopeEyebrow: '選択肢が広がります',
    subregionScopeTitle: '選択: {name}',
    subregionScopeBody: '国内全体から最適な商品を先に表示することもできます。そのほうが選択肢は多くなります。{name}の商品・価格・店舗を優先することもできます。',
    subregionScopeCountryFirst: '国内全体から表示',
    subregionScopeLocalFirst: '{name}を優先',
  },
};

export const regionMapUiContent: Record<LanguageType, RegionMapUiContent> =
  proxyLanguageRecord<RegionMapUiContent>(regionMapUiContentCore);

export const getRegionMapUiContent = (locale: LocaleDetails): RegionMapUiContent =>
  regionMapUiContent[locale.languageCode] ?? regionMapUiContent.en;

/**
 * Resolve `{token}` placeholders. Unknown tokens are left verbatim rather than
 * blanked, so a translation that invents a placeholder is visibly wrong in QA
 * instead of quietly dropping a word.
 */
export function fillRegionMapString(
  template: string,
  vars: Readonly<Record<string, string>>,
): string {
  return template.replace(/\{(\w+)\}/g, (match, key: string) =>
    Object.prototype.hasOwnProperty.call(vars, key) ? vars[key] : match,
  );
}
